import React from 'react'
import clsx from 'clsx'
import { Prompt } from 'react-router'
import { useAppDispatch } from '@SonarCX/hooks/redux'

import ControlledInfiniteTaskTableStateProvider from '@SonarCX/providers/ControlledInfiniteTaskTableStateProvider'

import TaskTable from '@SonarCX/pages/Tasks/TaskTable'
import TaskModal from '@SonarCX/components/molecules/ChatModals/TaskModal'

import { getSort } from './helpers'
import config from '@SonarCX/config'
import { KEY_MAP } from '@SonarCX/providers/ApiProvider/helpers'
import { DASHBOARD_TAB } from '@SonarCX/providers/TaskPageStateProvider'
import { CALLBACK_PHASES, useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import { clearSelected } from '@SonarCX/slices/allTasksTable'
import { useTaskSWRMutateCache } from '@SonarCX/providers/TaskSWRMutateCache'
import { useTaskComments } from '@SonarCX/providers/TaskCommentsProvider'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import useInfiniteFetch from '@SonarCX/hooks/useInfiniteFetch'
import useSavedValue from '@SonarCX/hooks/useSavedValue'
import useTaskIncludes from '../useTaskIncludes'
import useBaseStyles from '../styles'
import useStyles from './styles'

const getUrl = (type, id) => (page, per_page) => {
  const transformed = KEY_MAP[type]([
    type,
    id,
    `&page=${page}&per_page=${per_page}`
  ])
  const fullUrl = `${config.apiBaseUri}${transformed}`
  return fullUrl
}

const ASSIGNED_NAME = 'Assigned to me'
const ASSIGNED_OTHERS_NAME = 'Assigned to others'
const PRIVATE_NAME = 'My Private Tasks'

const DashboardTab = ({ pageRef }) => {
  const dispatch = useAppDispatch()

  const { user } = useEnvironment()
  const { isSmall } = useBreakpoint()
  const { updateCacheFromSWR, registerCallback } = useTaskAPI()
  const { setMutator, updateTasks } = useTaskSWRMutateCache()
  const { updateCounts } = useTaskComments()
  const [taskModalOpen, setTaskModalOpen] = React.useState(false)
  const [assignSelf, setAssignSelf] = React.useState(false)
  const [assignedToMeTableSort, setAssignedToMeTableSort] = useSavedValue({
    key: 'assignedToMeTableSort',
    defaultValue: '[]',
    useSession: true
  })

  const [assignedToOthersTableSort, setAssignedToOthersTableSort] =
    useSavedValue({
      key: 'assignedToOthersTableSort',
      defaultValue: '[]',
      useSession: true
    })

  const [privateTableSort, setPrivateTableSort] = useSavedValue({
    key: 'privateTableSort',
    defaultValue: '[]',
    useSession: true
  })

  const canFetch = React.useMemo(() => Boolean(user?.id), [user])

  const assignedIncludes = useTaskIncludes({ key: ASSIGNED_NAME })
  const {
    data: assignedToMe,
    loaded: assignedToMeLoaded,
    fullPagination: fullAssignedToMePagination,
    ...assignedToMePagination
  } = useInfiniteFetch(() => {
    if (!canFetch) {
      return null
    }

    const sort = getSort(assignedToMeTableSort)
    return `assignedTasks:${user.id}:${sort}${assignedIncludes}`
  })

  const assignedOthersIncludes = useTaskIncludes({ key: ASSIGNED_OTHERS_NAME })
  const {
    data: assignedToOthers,
    loaded: assignedToOthersLoaded,
    fullPagination: fullAssignedToOthersPagination,
    ...assignedToOthersPagination
  } = useInfiniteFetch(() => {
    if (!canFetch) {
      return null
    }

    const sort = getSort(assignedToOthersTableSort)
    return `assignedToOthersTasks:${user.id}:${sort}${assignedOthersIncludes}`
  })

  const privateIncludes = useTaskIncludes({ key: PRIVATE_NAME })
  const {
    data: ownTasks,
    loaded: ownTasksLoaded,
    fullPagination: fullOwnTasksPagination,
    ...ownTasksPagination
  } = useInfiniteFetch(() => {
    if (!canFetch) {
      return null
    }

    const sort = getSort(privateTableSort)
    return `ownTasks:${user.id}:${sort}${privateIncludes}`
  })

  const baseClasses = useBaseStyles({ smallMedia: isSmall })
  const classes = useStyles({
    smallMedia: isSmall,
    loaded: assignedToMeLoaded && assignedToOthersLoaded && ownTasksLoaded
  })

  const createTask = () => setTaskModalOpen(true)
  // Store tasks in internal cache
  React.useEffect(() => {
    updateCacheFromSWR(assignedToMe)
  }, [updateCacheFromSWR, assignedToMe])

  React.useEffect(() => {
    updateCacheFromSWR(assignedToOthers)
  }, [updateCacheFromSWR, assignedToOthers])

  React.useEffect(() => {
    updateCacheFromSWR(ownTasks)
  }, [updateCacheFromSWR, ownTasks])

  // Store comment counts
  React.useEffect(() => {
    if (assignedToMe) {
      updateCounts(assignedToMe.flatMap(x => x.data))
    }
  }, [updateCounts, assignedToMe])

  React.useEffect(() => {
    if (assignedToOthers) {
      updateCounts(assignedToOthers.flatMap(x => x.data))
    }
  }, [updateCounts, assignedToOthers])

  React.useEffect(() => {
    if (ownTasks) {
      updateCounts(ownTasks.flatMap(x => x.data))
    }
  }, [updateCounts, ownTasks])

  // Updates mutators
  React.useEffect(() => {
    setMutator(DASHBOARD_TAB, 'assignedTasks', assignedToMePagination.mutate)
  }, [setMutator, assignedToMePagination.mutate])

  React.useEffect(() => {
    setMutator(
      DASHBOARD_TAB,
      'assignedToOthersTasks',
      assignedToOthersPagination.mutate
    )
  }, [setMutator, assignedToOthersPagination.mutate])

  React.useEffect(() => {
    setMutator(DASHBOARD_TAB, 'ownTasks', ownTasksPagination.mutate)
  }, [setMutator, ownTasksPagination.mutate])

  // Register mutate functions to be called when task updates happen
  React.useEffect(() => {
    registerCallback(
      CALLBACK_PHASES.UPDATE,
      'DashboardTab-assigned',
      assignedToMePagination.mutate
    )
  }, [registerCallback, assignedToMePagination.mutate])

  React.useEffect(() => {
    registerCallback(
      CALLBACK_PHASES.UPDATE,
      'DashboardTab-others',
      assignedToOthersPagination.mutate
    )
  }, [registerCallback, assignedToOthersPagination.mutate])

  React.useEffect(() => {
    registerCallback(
      CALLBACK_PHASES.UPDATE,
      'DashboardTab-own',
      ownTasksPagination.mutate
    )
  }, [registerCallback, ownTasksPagination.mutate])

  const assignedToMePaginationProp = React.useMemo(
    () => ({
      ...fullAssignedToMePagination,
      urlFunc: getUrl('assignedTasks', user?.id)
    }),
    [fullAssignedToMePagination, user]
  )

  const assignedOthersPaginationProp = React.useMemo(
    () => ({
      ...fullAssignedToOthersPagination,
      urlFunc: getUrl('assignedToOthersTasks', user?.id)
    }),
    [fullAssignedToOthersPagination, user]
  )

  const myOwnAssignedPaginationProp = React.useMemo(
    () => ({
      ...fullOwnTasksPagination,
      urlFunc: getUrl('ownTasks', user?.id)
    }),
    [fullOwnTasksPagination, user]
  )

  return (
    <>
      <div className={baseClasses.root}>
        <div
          className={clsx(
            baseClasses.tasks,
            classes.tableHolder,
            classes.table
          )}
        >
          <ControlledInfiniteTaskTableStateProvider
            name={ASSIGNED_NAME}
            tasks={assignedToMe}
            collapsable={false}
            fullPagination={assignedToMePaginationProp}
            table={1}
          >
            <TaskTable
              id='self-assigned'
              isInfinite={true}
              pageRef={pageRef}
              loaded={assignedToMeLoaded}
              isPaginatedInfiniteLoader={true}
              initialSort={assignedToMeTableSort}
              setTableSort={setAssignedToMeTableSort}
              createTask={() => {
                setAssignSelf(true)
                createTask()
              }}
              multiple
              customFieldsToInclude={assignedIncludes}
              {...assignedToMePagination}
            />
          </ControlledInfiniteTaskTableStateProvider>

          <ControlledInfiniteTaskTableStateProvider
            name={ASSIGNED_OTHERS_NAME}
            tasks={assignedToOthers}
            collapsable={false}
            fullPagination={assignedOthersPaginationProp}
            table={2}
          >
            <TaskTable
              id='others-assigned'
              isInfinite={true}
              pageRef={pageRef}
              loaded={assignedToOthersLoaded}
              createTask={createTask}
              multiple
              isPaginatedInfiniteLoader={true}
              initialSort={assignedToOthersTableSort}
              setTableSort={setAssignedToOthersTableSort}
              customFieldsToInclude={assignedOthersIncludes}
              {...assignedToOthersPagination}
            />
          </ControlledInfiniteTaskTableStateProvider>

          <ControlledInfiniteTaskTableStateProvider
            name={PRIVATE_NAME}
            tasks={ownTasks}
            collapsable={false}
            fullPagination={myOwnAssignedPaginationProp}
            table={3}
          >
            <TaskTable
              id='private'
              isInfinite={true}
              pageRef={pageRef}
              loaded={ownTasksLoaded}
              createTask={createTask}
              multiple
              isPaginatedInfiniteLoader={true}
              initialSort={privateTableSort}
              setTableSort={setPrivateTableSort}
              customFieldsToInclude={privateIncludes}
              {...ownTasksPagination}
            />
          </ControlledInfiniteTaskTableStateProvider>
        </div>
      </div>

      {taskModalOpen && (
        <TaskModal
          open={taskModalOpen}
          onClose={() => {
            setAssignSelf(false)
            setTaskModalOpen(false)
          }}
          onTaskCreated={task => updateTasks(DASHBOARD_TAB, task)}
          assignSelf={assignSelf}
          canEdit
          canUpdate
        />
      )}

      <Prompt
        when
        message={() => {
          dispatch(clearSelected())
          return true
        }}
      />
    </>
  )
}

export default DashboardTab
